import { OverlayContainer } from '@angular/cdk/overlay';
import { APP_BASE_HREF } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { AngularFireModule, FIREBASE_APP_NAME, FIREBASE_OPTIONS } from '@angular/fire/compat';
import { SERVICE_WORKER } from '@angular/fire/compat/messaging';
import { MatIconRegistry } from '@angular/material/icon';
import { bootstrapApplication, DomSanitizer } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router } from '@angular/router';
import type { HttpInterceptorRouteConfig } from '@auth0/auth0-angular';
import { AuthClientConfig as Auth0ClientConfig, provideAuth0 } from '@auth0/auth0-angular';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import * as Sentry from '@sentry/angular';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { AppComponent } from 'app/app.component';
import { appRoutes } from 'app/app.routes';
import { CookieAuthInterceptor } from 'app/auth/interceptors/cookie-auth.interceptor';
import { UnifiedAuthInterceptor } from 'app/auth/interceptors/unified-auth.interceptor';
import { AuthOperationsService } from 'app/auth/services/auth-operations.service';
import { ElectricSQLService } from 'app/services/electric-sql.service';
import { UserInitService } from 'app/services/user-init.service';
import { DelegatingAuth0Service, EXTERNAL_AUTH_SERVICE, NoopAuth0Service, RedirectingAuthService } from 'app/auth/services/external-auth.service';
import { ConsentMenuOverlay } from 'app/consent-menu-overlay';
import { provideErrorHandler } from 'app/error-handler';
import { HttpParamEncodingInterceptor } from 'app/http-param-encoding.interceptor';
import { reducers } from 'app/ngrx/app-state/app-state';
import { IntrosEffects } from 'app/ngrx/intros/effects/intros.effects';
import { KioskEffects } from 'app/ngrx/kiosk/kiosk.effects';
import { ProcessEffects } from 'app/ngrx/onboard-process/effects/process.effects';
import { RepresentedUsersEffects } from 'app/ngrx/represented-users/effects/represented-users.effects';
import { SchoolsEffects } from 'app/ngrx/schools/effects/schools.effects';
import { ToastEffects } from 'app/ngrx/toast/effects/toast.effects';
import { UserEffects } from 'app/ngrx/user/effects/user.effects';
import { ProgressInterceptor } from 'app/progress-interceptor';
import { initNavigationTracker } from 'app/services/chunk-error-handler';
import 'firebase/compat/messaging';
import { KeyboardShortcutsModule } from 'ng-keyboard-shortcuts';
import { CookieService } from 'ngx-cookie-service';
import type { AppConfig } from './app/config/app.config';
import { APP_CONFIG } from './app/config/app.config';
import { loadConfig } from './app/config/load-config';
import { initSentry } from './initSentry';
import { ngrxDevtoolsProviders } from './ngrx-devtools';

async function bootstrap(): Promise<void> {
	const config = loadConfig();

	initSentry(config);

	await bootstrapApplication(AppComponent, {
		providers: [
			provideZoneChangeDetection(),
			{ provide: APP_CONFIG, useValue: config },
			{ provide: APP_BASE_HREF, useValue: config.basePath },
			{ provide: OverlayContainer, useClass: ConsentMenuOverlay },
			{ provide: HTTP_INTERCEPTORS, useClass: UnifiedAuthInterceptor, multi: true },
			{ provide: HTTP_INTERCEPTORS, useClass: ProgressInterceptor, multi: true },
			{ provide: HTTP_INTERCEPTORS, useClass: HttpParamEncodingInterceptor, multi: true },
			provideAppInitializer(() => {
				const router = inject(Router);
				return initNavigationTracker(router, config.basePath);
			}),
			provideAppInitializer(() => {
				const registry = inject(MatIconRegistry);
				const sanitizer = inject(DomSanitizer);
				registry.addSvgIconResolver((name) => sanitizer.bypassSecurityTrustResourceUrl(`assets/backpack-icons/${name}.svg`));
			}),
			{
				provide: Sentry.TraceService,
				deps: [Router],
			},
			// Eagerly instantiate TraceService, UserInitService and ElectricSQLService before routing starts.
			// TraceService hooks into router events for Sentry tracing. UserInitService registers
			// its auth effect and showUI/preloader gate here so loading begins as soon as auth state
			// is known, before any component mounts. ElectricSQLService shadow-syncs hall passes
			// for flagged schools, which no component asks for.
			provideAppInitializer(() => {
				inject(Sentry.TraceService);
				inject(UserInitService);
				inject(ElectricSQLService);
			}),
			provideAppInitializer(() => inject(AuthOperationsService).processOAuthRedirect()),
			provideAnimations(),
			provideAuth0(),
			provideErrorHandler(),
			provideHttpClient(withInterceptorsFromDi()),
			provideRouter(appRoutes),
			provideStore(reducers, {
				runtimeChecks: {
					strictStateImmutability: false,
					strictActionImmutability: false,
				},
			}),
			provideEffects(ProcessEffects, UserEffects, IntrosEffects, SchoolsEffects, KioskEffects, RepresentedUsersEffects, ToastEffects),
			provideTanStackQuery(new QueryClient()),
			...ngrxDevtoolsProviders(config),
			importProvidersFrom(KeyboardShortcutsModule.forRoot(), {
				ngModule: AngularFireModule,
				providers: [
					{ provide: FIREBASE_OPTIONS, useFactory: () => config.firebase },
					{ provide: FIREBASE_APP_NAME, useValue: 'notifyhallpass' },
				],
			}),
			{
				// Firebase Messaging uses SERVICE_WORKER to locate the push SW.
				// When basePath is not '/', the app is deployed at a sub-path (e.g. /smartpass on
				// Raptor domains), so firebase-messaging-sw.js lives there, not at the origin root.
				// Without this, Firebase tries /firebase-messaging-sw.js and gets a 404.
				provide: SERVICE_WORKER,
				useFactory: () => {
					if ('serviceWorker' in navigator && config.basePath !== '/') {
						const basePath = config.basePath.replace(/\/$/, '');
						return navigator.serviceWorker.register(`${basePath}/firebase-messaging-sw.js`);
					}
					return Promise.resolve(null);
				},
			},
			{
				provide: EXTERNAL_AUTH_SERVICE,
				useFactory: (ac: AppConfig, acc: Auth0ClientConfig) => {
					// If Auth0 is configured, use the full Auth0 implementation
					if (ac.auth0) {
						const route = (url: string, anon = false): HttpInterceptorRouteConfig => ({
							uri: (url.endsWith('/') ? url : url + '/') + '*',
							allowAnonymous: anon,
						});

						acc.set({
							clientId: ac.auth0.clientId,
							domain: ac.auth0.domain,
							authorizationParams: {
								redirect_uri: window.location.origin + ac.basePath + '/auth0-callback',
								audience: ac.auth0.audience,
							},
							cacheLocation: 'localstorage',
							httpInterceptor: {
								allowedList: [route(ac.auth0.audience), route(ac.staticApiRoot || ac.auth0.server.api_root, true)],
							},
						});

						return new DelegatingAuth0Service();
					}

					// If raptorSmartpassUrl is configured, redirect Auth0 users there
					if (ac.raptorSmartpassUrl) {
						return new RedirectingAuthService(ac.raptorSmartpassUrl);
					}

					// No Auth0 config and no redirect URL - use Noop
					return new NoopAuth0Service();
				},
				deps: [APP_CONFIG, Auth0ClientConfig],
			},
			CookieService,
			CookieAuthInterceptor,
		],
	});
}

bootstrap().catch((err: unknown) => console.error('Failed to bootstrap application:', err));
